import React, { useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Settings2, ImageIcon, Inbox, UserCog, ScrollText, Users, BellRing, ShieldCheck, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { useAuth } from "../contexts/AuthContext";
import { useWorkspaceBranding } from "../contexts/WorkspaceBrandingContext";
import { useNavigationGuard } from "../contexts/NavigationGuardContext";

const workspaceLinks = [
    ["/dashboard", "Dashboard", LayoutDashboard, "dashboard"],
    ["/leads", "Leads", Inbox, "leads"],
    ["/settings", "Business data", Settings2, "settings"],
    ["/photos", "Photos & requests", ImageIcon, "photos"],
    ["/account", "Account & privacy", UserCog, "account"],
];
const adminLinks = [
    ["/admin/inbox", "Inbox", BellRing, "admin-inbox"],
    ["/admin/clients", "Clients", Users, "admin-clients"],
    ["/admin", "Admin overview", ShieldCheck, "admin"],
    ["/admin/audit", "Audit log", ScrollText, "admin-audit"],
];

export const MobileNavigation = ({ open, onOpenChange, triggerRef }) => {
    const navigate = useNavigate();
    const afterClose = useRef(null);
    const { user, logout } = useAuth();
    const { businessName } = useWorkspaceBranding();
    const { requestAction } = useNavigationGuard();
    const closeThen = (action) => {
        if (afterClose.current) return;
        afterClose.current = action;
        onOpenChange(false);
    };
    const finishClose = (event) => {
        event.preventDefault();
        triggerRef.current?.focus({ preventScroll: true });
        const action = afterClose.current;
        afterClose.current = null;
        // Radix has released the Sheet focus scope before another route's
        // unsaved-change dialog can open. No timer tied to animation duration.
        if (action) requestAnimationFrame(action);
    };
    const renderLinks = (links) => links.map(([to, label, Icon, id]) => (
        <NavLink key={to} to={to} end={to === "/admin"} data-testid={`mobile-menu-${id}`}
            onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
                event.preventDefault();
                closeThen(() => navigate(to));
            }}
            className={({ isActive }) => `portal-menu-link ${isActive ? "portal-nav-active" : ""}`}>
            <Icon className="h-[18px] w-[18px] shrink-0" /><span>{label}</span>
        </NavLink>
    ));
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" closeTestId="mobile-menu-close" className="portal-mobile-menu" data-testid="mobile-navigation-menu" overlayClassName="z-[80]" overlayTestId="mobile-menu-overlay" onCloseAutoFocus={finishClose}>
                <SheetHeader className="pr-12 text-left">
                    <SheetTitle data-testid="mobile-menu-title">Your workspace</SheetTitle>
                    <SheetDescription data-testid="mobile-menu-business-name" className="break-words">{businessName}</SheetDescription>
                </SheetHeader>
                <nav className="mt-6 space-y-1" aria-label="Workspace navigation" data-testid="mobile-menu-workspace-nav">
                    {renderLinks(workspaceLinks)}
                </nav>
                {user?.role === "admin" && <nav className="mt-5 border-t border-zinc-800 pt-4 space-y-1" aria-label="WebJesters administration" data-testid="mobile-menu-admin-nav">
                    <p className="px-3 pb-2 text-xs text-zinc-500">WebJesters administration</p>
                    {renderLinks(adminLinks)}
                </nav>}
                <div className="mt-5 border-t border-zinc-800 pt-4">
                    <p className="px-3 text-xs text-zinc-500 break-all" data-testid="mobile-menu-user-email">{user?.email}</p>
                    <button type="button" data-testid="mobile-logout-button" className="portal-menu-link mt-2 w-full"
                        onClick={() => closeThen(() => requestAction(logout))}>
                        <LogOut className="h-[18px] w-[18px]" /> Sign out
                    </button>
                </div>
            </SheetContent>
        </Sheet>
    );
};